import React, { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';

// Stores
import { useAuthStore } from './stores/authStore';
import api from './services/api';

// Pages
import HomePage from './pages/HomePage';
import AboutUsPage from './pages/AboutUsPage';
import ContactPage from './pages/ContactPage';
import TermsOfUsePage from './pages/TermsOfUsePage';
import RegisterPage from './pages/RegisterPage';
// TEMPORARY: OTP verification is bypassed.
// import VerifyEmailPage from './pages/VerifyEmailPage';
import LoginPage from './pages/LoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ChangePasswordPage from './pages/ChangePasswordPage';
import DashboardPage from './pages/DashboardPage';
import ProfileDetailPage from './pages/ProfileDetailPage';
import MyConnectionsPage from './pages/MyConnectionsPage';
import WalletPage from './pages/WalletPage';
import AdminDashboardPage from './pages/AdminDashboardPage';
import AdminRechargeManagementPage from './pages/AdminRechargeManagementPage';
import AdminRechargeHistoryPage from './pages/AdminRechargeHistoryPage';
import AdminProfileReviewPage from './pages/AdminProfileReviewPage';
import AdminUserManagementPage from './pages/AdminUserManagementPage';
import AdminInactiveUsersPage from './pages/AdminInactiveUsersPage';
import AdminRegistrationFieldsPage from './pages/AdminRegistrationFieldsPage';
import AdminUserTimelinePage from './pages/AdminUserTimelinePage';
import AdminReportsPage from './pages/AdminReportsPage';
import AdminContactInquiriesPage from './pages/AdminContactInquiriesPage';
import AdminSubadminsPage from './pages/AdminSubadminsPage';
import ActivationStatusPage from './pages/ActivationStatusPage';
import AdminActivationStatusPage from './pages/AdminActivationStatusPage';
import AdminWalletRulesPage from './pages/AdminWalletRulesPage';
import AdminFilterOptionsPage from './pages/AdminFilterOptionsPage';
import AdminDailyRecommendationsPage from './pages/AdminDailyRecommendationsPage';

// Components
import Navbar from './components/Navbar';
import PublicNavbar from './components/PublicNavbar';
import ProtectedRoute from './components/ProtectedRoute';
import AdminRoute from './components/AdminRoute';

const PENDING_MEMBERSHIP_ID_KEY = 'maandvo_pending_membership_id';

function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, userRole, user, checkAuth, authResolved, logout, recordActivity, isSessionIdle } = useAuthStore();
  const resolvedRole = userRole || user?.role || 'USER';
  const hasPendingMembershipConfirmation = Boolean(sessionStorage.getItem(PENDING_MEMBERSHIP_ID_KEY));
  const postLoginPath = ['SUPER_ADMIN', 'CONTENT_MODERATOR', 'FINANCE_ADMIN', 'SUPPORT_ADMIN', 'ID_VERIFICATION_ADMIN'].includes(resolvedRole)
    ? '/admin'
    : '/';
  const usesStandaloneDashboardShell = isAuthenticated && (
    location.pathname.startsWith('/admin')
    || ['/dashboard', '/my-profile', '/browse-profiles', '/activation-status', '/wallet', '/my-connections', '/change-password'].includes(location.pathname)
    || location.pathname.startsWith('/profile/')
  );

  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    const path = location.pathname;
    if (path !== '/register') {
      sessionStorage.removeItem(PENDING_MEMBERSHIP_ID_KEY);
    }
    let pageTitle = 'Maandvo Matrimony';

    if (path === '/about-us') pageTitle = 'About Us | Maandvo Matrimony';
    else if (path === '/contact') pageTitle = 'Contact Us | Maandvo Matrimony';
    else if (path === '/terms-of-use') pageTitle = 'Terms of Use | Maandvo Matrimony';
    else if (path === '/login') pageTitle = 'Login | Maandvo Matrimony';
    else if (path === '/register') pageTitle = 'Register | Maandvo Matrimony';
    else if (path === '/forgot-password' || path === '/forgot-password/verify') pageTitle = 'Forgot Password | Maandvo Matrimony';
    else if (path === '/my-profile' || path === '/dashboard') pageTitle = 'My Profile | Maandvo Matrimony';
    else if (path === '/browse-profiles') pageTitle = 'Browse Profiles | Maandvo Matrimony';
    else if (path.startsWith('/profile/')) pageTitle = 'Profile Details | Maandvo Matrimony';
    else if (path === '/my-connections') pageTitle = 'My Connections | Maandvo Matrimony';
    else if (path === '/wallet') pageTitle = 'Wallet | Maandvo Matrimony';
    else if (path === '/activation-status') pageTitle = 'Activation Status | Maandvo Matrimony';
    else if (path.startsWith('/admin')) pageTitle = 'Admin | Maandvo Matrimony';

    document.title = pageTitle;
  }, [location.pathname]);

  useEffect(() => {
    const VISIT_KEY = 'mb_visit_session_key';
    let sessionKey = sessionStorage.getItem(VISIT_KEY);
    if (!sessionKey) {
      sessionKey = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      sessionStorage.setItem(VISIT_KEY, sessionKey);
    }
    void api.post('/users/public/track-visit', { session_key: sessionKey }).catch(() => undefined);
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;
    const activityEvents = ['click', 'keydown', 'pointerdown', 'scroll', 'touchstart'];
    const record = () => recordActivity();
    const checkIdle = () => {
      if (!isSessionIdle()) return;
      const requestedPath = `${location.pathname}${location.search}${location.hash}`;
      sessionStorage.removeItem(PENDING_MEMBERSHIP_ID_KEY);
      logout();
      navigate('/login', { replace: true, state: { from: requestedPath } });
    };
    activityEvents.forEach((eventName) => window.addEventListener(eventName, record, { passive: true }));
    const intervalId = window.setInterval(checkIdle, 30_000);
    return () => {
      activityEvents.forEach((eventName) => window.removeEventListener(eventName, record));
      window.clearInterval(intervalId);
    };
  }, [isAuthenticated, location.pathname, location.search, location.hash, navigate, logout, recordActivity, isSessionIdle]);

  if (!authResolved) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {isAuthenticated ? (!usesStandaloneDashboardShell && <Navbar />) : <PublicNavbar />}

      <Box sx={{ flex: 1 }}>
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<HomePage />} />
          <Route path="/about-us" element={<AboutUsPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/terms-of-use" element={<TermsOfUsePage />} />
          <Route
            path="/register"
            element={!isAuthenticated || hasPendingMembershipConfirmation ? <RegisterPage /> : <Navigate to={postLoginPath} />}
          />
          {/* TEMPORARY: OTP verification is bypassed. */}
          {/* <Route path="/verify-email" element={!isAuthenticated ? <VerifyEmailPage /> : <Navigate to={postLoginPath} />} /> */}
          <Route path="/login" element={!isAuthenticated ? <LoginPage /> : <Navigate to={postLoginPath} />} />
          <Route path="/forgot-password" element={!isAuthenticated ? <ForgotPasswordPage /> : <Navigate to={postLoginPath} />} />
          <Route path="/forgot-password/verify" element={!isAuthenticated ? <ForgotPasswordPage /> : <Navigate to={postLoginPath} />} />

          <Route path="/change-password" element={<ProtectedRoute><ChangePasswordPage /></ProtectedRoute>} />

          {/* Protected Routes */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/my-profile"
            element={
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/my-live-profile"
            element={
              <ProtectedRoute>
                {user?.id ? (
                  <Navigate to={`/profile/${user.profile_id || user.id}`} state={{ from: '/my-profile' }} replace />
                ) : (
                  <Navigate to="/my-profile" replace />
                )}
              </ProtectedRoute>
            }
          />
          <Route
            path="/browse-profiles"
            element={
              <ProtectedRoute>
                <Navigate to="/" replace />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile/:id"
            element={
              <ProtectedRoute>
                <ProfileDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/my-connections"
            element={
              <ProtectedRoute>
                <MyConnectionsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/wallet"
            element={
              <ProtectedRoute>
                <WalletPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/activation-status"
            element={
              <ProtectedRoute>
                <ActivationStatusPage />
              </ProtectedRoute>
            }
          />

          {/* Admin Routes */}
          <Route path="/admin/inactive-users" element={<AdminRoute><AdminInactiveUsersPage /></AdminRoute>} />
          <Route
            path="/admin"
            element={
              <AdminRoute>
                <AdminDashboardPage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/recharge-management"
            element={
              <AdminRoute>
                <AdminRechargeManagementPage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/recharge-history"
            element={
              <AdminRoute>
                <AdminRechargeHistoryPage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/profile-reviews"
            element={
              <AdminRoute>
                <AdminProfileReviewPage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/reports"
            element={
              <AdminRoute>
                <AdminReportsPage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/contact-inquiries"
            element={
              <AdminRoute>
                <AdminContactInquiriesPage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/subadmins"
            element={
              <AdminRoute>
                <AdminSubadminsPage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/users"
            element={
              <AdminRoute>
                <AdminUserManagementPage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/registration-fields"
            element={
              <AdminRoute>
                <AdminRegistrationFieldsPage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/user-control-center"
            element={
              <AdminRoute>
                <AdminUserTimelinePage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/activation-status"
            element={
              <AdminRoute>
                <AdminActivationStatusPage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/wallet-rules"
            element={
              <AdminRoute>
                <AdminWalletRulesPage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/filter-options"
            element={
              <AdminRoute>
                <AdminFilterOptionsPage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/daily-recommendations"
            element={
              <AdminRoute>
                <AdminDailyRecommendationsPage />
              </AdminRoute>
            }
          />

          {/* 404 */}
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </Box>
    </Box>
  );
}

export default App;
